// Where a subtree stands relative to a surface's live preview (route-preview-router.tsx), without loading that router.

import { useRouter } from '@tanstack/react-router'
import type { RoutePath } from './surface.config'

// A layout hosting a NESTED surface must render only its <Outlet/> when this equals its own path, or the preview
// repeats that layout's chrome inside the tab-sized pane it is supposed to fill.
export function useTransitionPreviewFrom(): RoutePath | null {
  const context = useRouter().options.context as { previewFrom?: RoutePath }
  return context.previewFrom ?? null
}

// A DOCUMENT id belongs to the pane the reader is on: a preview's second copy would make `#anchor` and
// getElementById resolve against a pane nobody is looking at. A preview renders the markup without the ids.
export function useIsLivePane(): boolean {
  return useTransitionPreviewFrom() === null
}

/**
 * 'passthrough' is chrome AROUND the previewed pane (render only the path to the surface), 'above' is content inside
 * someone else's pane, 'aside' an unrelated branch. BY SEGMENT, never by string prefix, which '/' is of everything.
 */
export type PreviewMode = 'live' | 'passthrough' | 'above' | 'aside'

export function usePreviewMode(scope: RoutePath): PreviewMode {
  const previewFrom = useTransitionPreviewFrom()
  if (previewFrom === null) return 'live'
  if (previewFrom === scope || isUnder(previewFrom, scope)) return 'passthrough'
  return isUnder(scope, previewFrom) ? 'above' : 'aside'
}

/** Is `path` strictly below `base`, counted in SEGMENTS — `/teams/42` is under `/teams`, `/teams-archive` isn't. */
export function isUnder(path: RoutePath, base: RoutePath): boolean {
  if (base === '/') return path !== '/'
  return path.startsWith(base.endsWith('/') ? base : `${base}/`)
}
